"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useKeenSlider} from "keen-slider/react";

import {ButtonWithImage} from "@/shared/ui/buttons";
import {assetPath} from "@/shared/utils/asset-path";

import {useKeyboardNav} from "../hooks/use-keyboard-nav";
import {GalleryImageWithSpinner} from "./gallery-image-with-spinner";

import "./styles/image-slider.scss";
import "keen-slider/keen-slider.min.css";

export interface ImageSliderItem {
    src: string;
    alt: string;
}

export interface ImageSliderProps {
    images: Array<ImageSliderItem>;
    initialIndex?: number;
    onClose?: () => void;
    className?: string;
}

/**
 * Image slider component with keyboard navigation
 * Uses keen-slider for smooth sliding experience
 */
export function ImageSlider({images, initialIndex = 0, onClose, className = ""}: ImageSliderProps): JSX.Element {
    const [currentSlide, setCurrentSlide] = useState(initialIndex);

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        initial: initialIndex,
        loop: true,
        slides: {
            perView: 1,
        },
        rubberband: true,
        slideChanged(s) {
            setCurrentSlide(s.track.details.rel);
        },
    });

    // Keyboard navigation
    useKeyboardNav({
        onPrev: () => slider.current?.prev(),
        onNext: () => slider.current?.next(),
        onClose,
        enabled: true,
    });

    const handlePrev = (): void => {
        slider.current?.prev();
    };

    const handleNext = (): void => {
        slider.current?.next();
    };

    return (
        <div className={`image-slider ${className}`}>
            <div ref={sliderRef} className="keen-slider image-slider__track">
                {images.map((img, index) => (
                    <div className="keen-slider__slide" key={`${img.src}-slide-${index}`}>
                        <div className="image-slider__slide-inner">
                            <GalleryImageWithSpinner
                                src={img.src}
                                alt={img.alt || `Image ${index + 1}`}
                                width={1300}
                                height={800}
                                quality={80}
                                priority={false}
                                className="image-slider__slide-image"
                                sizes="100vw"
                                loading="lazy"
                                fillContainer
                                noImgixCrop={true}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="image-slider__controls">
                <div className="image-slider__counter">
                    {currentSlide + 1} / {images.length}
                </div>

                <div className="image-slider__nav">
                    <ButtonWithImage
                        className="image-slider__nav-button image-slider__nav-button--prev"
                        variant="primary"
                        onClick={handlePrev}
                        aria-label="Previous image"
                    >
                        <SmartImage
                            src={assetPath("/images/icons/navigation/arrow-left.svg")}
                            alt="arrow"
                            width={16}
                            height={16}
                            aria-hidden="true"
                        />
                    </ButtonWithImage>
                    <ButtonWithImage
                        className="image-slider__nav-button image-slider__nav-button--next"
                        variant="primary"
                        onClick={handleNext}
                        aria-label="Next image"
                    >
                        <SmartImage
                            src={assetPath("/images/icons/navigation/arrow-right.svg")}
                            alt="arrow"
                            width={16}
                            height={16}
                            aria-hidden="true"
                        />
                    </ButtonWithImage>
                </div>
            </div>
        </div>
    );
}
